import { useState } from "react";
import { X, Loader2, CheckCircle2, XCircle, HelpCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * DuplicateReviewDialog — 2026-06-20 (user request). Daftar pasangan proyek yang
 * dicurigai duplikat beserta ALASAN deteksi (nama ternormalisasi, client, nomor
 * kontrak/PO). Reviewer memilih: Duplikat Valid / Bukan Duplikat / Perlu Review.
 * Keputusan tersimpan di server — pasangan "bukan duplikat"/"duplikat valid"
 * tidak lagi dihitung di angka peringatan. Tidak ada hapus/gabung otomatis.
 */

const STATUS_META = {
  perlu_review: { label: "Perlu Review", cls: "bg-amber-50 border-amber-300 text-amber-800", icon: HelpCircle },
  duplikat_valid: { label: "Duplikat Valid", cls: "bg-rose-50 border-rose-300 text-rose-700", icon: CheckCircle2 },
  bukan_duplikat: { label: "Bukan Duplikat", cls: "bg-emerald-50 border-emerald-300 text-emerald-700", icon: XCircle },
};

export default function DuplicateReviewDialog({ open, onOpenChange, data, onReviewed, canReview }) {
  const [busyKey, setBusyKey] = useState(null);
  const [notes, setNotes] = useState({});
  if (!open) return null;
  const pairs = data?.pairs || [];
  const counts = data?.counts || { perlu_review: 0, duplikat_valid: 0, bukan_duplikat: 0 };

  const review = async (pairKey, status) => {
    setBusyKey(pairKey + status);
    try {
      await api.post("/projects/duplicates/review", { pair_key: pairKey, status, note: notes[pairKey] || null });
      toast.success("Keputusan review tersimpan");
      onReviewed?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan review");
    } finally { setBusyKey(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4" data-testid="dupe-dialog">
      <div className="bg-white border border-slate-300 w-full max-w-4xl shadow-xl">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm font-bold text-[#0B1D3A]">Review Kemungkinan Proyek Duplikat</div>
            <div className="text-[10px] text-slate-500 mt-0.5" data-testid="dupe-counts">
              Perlu Review: <b className="text-amber-700">{counts.perlu_review}</b> · Duplikat Valid: <b className="text-rose-700">{counts.duplikat_valid}</b> · Bukan Duplikat: <b className="text-emerald-700">{counts.bukan_duplikat}</b>
            </div>
          </div>
          <button type="button" onClick={() => onOpenChange(false)} data-testid="dupe-close"
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="px-4 py-2 border-b border-slate-100 text-[10px] text-slate-500 bg-slate-50">
          Aturan deteksi: nama proyek ternormalisasi + client identik, atau nomor kontrak/PO identik. Client yang sama saja <b>tidak</b> dianggap duplikat. Review tidak menghapus/menggabungkan data.
        </div>
        <div className="max-h-[65vh] overflow-y-auto">
          {pairs.length === 0 ? (
            <div className="p-8 text-center text-[11px] text-slate-400" data-testid="dupe-empty">Tidak ada kandidat duplikat pada universe saat ini.</div>
          ) : pairs.map((p) => {
            const sm = STATUS_META[p.status] || STATUS_META.perlu_review;
            const Icon = sm.icon;
            return (
              <div key={p.pair_key} className="px-4 py-3 border-b border-slate-100" data-testid={`dupe-pair-${p.pair_key.replace("|", "_")}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-[#0B1D3A]">
                      {p.project_a.project_name} <span className="text-slate-400 font-normal">vs</span> {p.project_b.project_name}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 font-mono" data-testid={`dupe-ids-${p.pair_key.replace("|", "_")}`}>
                      ID: {p.project_a.id} · {p.project_b.id}
                    </div>
                    <div className="text-[10px] text-slate-600 mt-1">
                      Client: {p.project_a.client || "—"} / {p.project_b.client || "—"}
                      {(p.project_a.contract_number || p.project_b.contract_number) && (
                        <> · Kontrak/PO: {p.project_a.contract_number || "—"} / {p.project_b.contract_number || "—"}</>
                      )}
                    </div>
                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                      {p.reasons.map((r) => (
                        <span key={r} className="text-[9px] uppercase tracking-wider font-bold px-1 py-px border bg-sky-50 border-sky-300 text-sky-800" data-testid={`dupe-reason-${p.pair_key.replace("|", "_")}`}>{r}</span>
                      ))}
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border shrink-0 ${sm.cls}`} data-testid={`dupe-status-${p.pair_key.replace("|", "_")}`}>
                    <Icon size={10} /> {sm.label}
                  </span>
                </div>
                {p.reviewed_by_name && (
                  <div className="text-[9px] text-slate-400 mt-1">Direview oleh {p.reviewed_by_name} · {String(p.reviewed_at || "").slice(0, 10)}{p.note ? ` · "${p.note}"` : ""}</div>
                )}
                {canReview && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <input value={notes[p.pair_key] || ""} onChange={(e) => setNotes((s) => ({ ...s, [p.pair_key]: e.target.value }))}
                      placeholder="Catatan review (opsional)" data-testid={`dupe-note-${p.pair_key.replace("|", "_")}`}
                      className="flex-1 min-w-[180px] border border-slate-300 px-2 h-7 text-[11px] focus:outline-none focus:border-[#0B1D3A]" />
                    {["duplikat_valid", "bukan_duplikat", "perlu_review"].map((st) => (
                      <button key={st} type="button" disabled={busyKey !== null || p.status === st}
                        onClick={() => review(p.pair_key, st)}
                        data-testid={`dupe-btn-${st}-${p.pair_key.replace("|", "_")}`}
                        className={`px-2 py-1 text-[9px] uppercase tracking-wider font-bold border disabled:opacity-40 ${STATUS_META[st].cls} hover:brightness-95`}>
                        {busyKey === p.pair_key + st ? <Loader2 size={10} className="animate-spin inline" /> : STATUS_META[st].label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
